import { useEffect, useRef } from 'react';
import { useSEO, useSchemaLD } from '@/hooks/useSEO';

const benefits = [
  {
    icon: 'ri-vip-crown-line',
    title: 'Seu escritório reflete o tamanho do seu sucesso',
    description: 'Mesas de reunião imponentes, cadeiras presidenciais e ambientes que transmitem autoridade para fornecedores, parceiros e clientes. Cada detalhe comunica poder.',
  },
  {
    icon: 'ri-building-4-line',
    title: 'Especialistas em escritórios de diretoria',
    description: 'Distribuidoras, construtoras, escritórios de advocacia e empresas de médio e grande porte. Ambientes de comando que fecham negócios antes de qualquer palavra.',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Projeto 3D gratuito do seu escritório',
    description: 'Antes de fabricar qualquer peça, você aprova o projeto completo em 3D. Vê exatamente como vai ficar a sala de reunião, a diretoria e a recepção.',
  },
  {
    icon: 'ri-tools-line',
    title: 'Equipe própria de montagem',
    description: 'Instalação feita pela nossa equipe, no horário que sua empresa preferir. Sem terceiros, sem imprevistos. Você recebe o ambiente pronto para receber clientes.',
  },
];

const blocks = [
  {
    title: 'Seu Escritório Deve Refletir o Tamanho do Seu Sucesso',
    text: [
      'Um fornecedor que entra na sua sala de reunião e vê uma mesa de MDF barata, cadeiras desgastadas e iluminação fraca já forma uma opinião sobre sua empresa — e essa opinião afeta a negociação. Um escritório de comando bem projetado comunica solidez, profissionalismo e poder antes de qualquer apresentação começar.',
      'Nossos escritórios de diretoria utilizam mesas de reunião em MDF de alta espessura com tampo em vidro temperado ou laminado premium, cadeiras presidenciais com couro legítimo ou courvin de alta qualidade, estrutura em alumínio ou aço cromado, e mecanismo de reclinação com travamento em múltiplas posições. O ambiente completo — da recepção à sala de reunião — projetado para transmitir a autoridade que sua empresa conquistou.',
    ],
    img: '/media/ia/0325.webp',
    reverse: false,
  },
  {
    title: 'Salas de Reunião que Fecham Negócios',
    text: [
      'A sala de reunião é onde os grandes negócios acontecem. Ela precisa de mesa que acomode de 8 a 20 pessoas com conforto, cadeiras que mantenham o executivo alerta e confortável por horas, painel para TV ou projetor integrado ao mobiliário, e iluminação que pode ser regulada conforme o tipo de reunião — apresentação, negociação ou brainstorming.',
      'Também projetamos salas de reunião menores para reuniões de diretoria com 4 a 6 pessoas, com mesa redonda ou oval que elimina hierarquia visual e facilita a tomada de decisão. E salas de videoconferência com fundo neutro, iluminação frontal e acústica tratada por divisórias que garantem qualidade de imagem e som profissional.',
    ],
    img: 'https://readdy.ai/api/search-image?query=Modern%20executive%20conference%20room%20with%20large%20imposing%20table%2C%20premium%20leather%20chairs%2C%20dark%20wood%20and%20glass%20design%2C%20sophisticated%20corporate%20meeting%20room%2C%20high-end%20business%20furniture&width=800&height=500&seq=mcorp_v2_2&orientation=landscape',
    reverse: true,
  },
  {
    title: 'Recepção Premium e Ambientes que Impressionam do Primeiro Contato',
    text: [
      'A recepção é onde o cliente forma a primeira impressão da sua empresa. Nossos projetos incluem balcão de recepção com iluminação LED embutida e logo da empresa em relevo, sofás e poltronas de design que convidam à espera confortável, painéis de madeira com textura que transmitem solidez e sofisticação, e piso vinílico ou carpete que absorve o som dos passos e cria um ambiente mais silencioso e profissional.',
      'Para distribuidoras e construtoras, também projetamos salas de apresentação de produtos com expositores iluminados, mesas de análise de projetos com tampo amplo e iluminação técnica, e áreas de convivência para equipes comerciais com sofás modulares e mesas de apoio. Tudo com o mesmo padrão premium — do portão de entrada à sala do diretor.',
    ],
    img: '/media/ia/0327.webp',
    reverse: false,
  },
];

const galleryImages = [
  {
    src: '/media/ia/0328.webp',
    alt: 'Mesa executiva com cadeira presidencial',
  },
  {
    src: '/media/ia/0329.webp',
    alt: 'Sala de reunião executiva',
  },
  {
    src: '/media/ia/0330.webp',
    alt: 'Recepção corporativa premium',
  },
  {
    src: '/media/ia/0331.webp',
    alt: 'Lounge executivo',
  },
];

export default function MarcenariaCorporativaPage() {
  const whatsappBtnRef = useRef<HTMLAnchorElement>(null);

  useSEO({
    title: 'Escritórios de Diretoria e Salas de Reunião Premium | Caruaru PE',
    description: 'Escritórios de comando, salas de reunião imponentes e recepções premium para distribuidoras e construtoras. Projeto 3D gratuito. Caruaru e Agreste PE.',
    keywords: 'escritório diretoria, sala reunião executiva, móveis corporativos premium, distribuidora construtora, Caruaru PE',
    path: '/marcenaria-corporativa',
  });

  useSchemaLD({
    id: 'schema-marcenaria-corporativa',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Escritórios de Diretoria e Salas de Reunião Premium | Caruaru PE',
      description: 'Escritórios de comando, salas de reunião imponentes e recepções premium para distribuidoras e construtoras. Projeto 3D gratuito.',
      url: `${import.meta.env.VITE_SITE_URL ?? 'https://homeofficepremium.com'}/marcenaria-corporativa`,
      about: {
        '@type': 'Service',
        name: 'Escritórios de Diretoria Premium',
        provider: {
          '@type': 'Organization',
          name: 'Home Office Premium',
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Av. Agamenon Magalhães, 1519',
            addressLocality: 'Caruaru',
            addressRegion: 'PE',
            postalCode: '55014-000',
            addressCountry: 'BR',
          },
        },
      },
    },
  });

  return (
    <div className="lp-page-wrapper min-h-screen bg-white">
      <section
        className="relative min-h-[640px] md:min-h-[700px] flex items-center"
        style={{
          backgroundImage: 'url(/media/ia/0332.webp)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-16 w-full">
          <div className="max-w-2xl">
            <span className="inline-block bg-[#E8610A] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider mb-5">
              Escritórios de Estratégia · Distribuidoras · Construtoras
            </span>
            <h1 className="text-white text-4xl md:text-5xl font-bold leading-tight mb-4">
              Seu escritório deve refletir o tamanho do seu sucesso.
            </h1>
            <p className="text-white/85 text-lg leading-relaxed mb-3">
              Mesas de reunião imponentes, cadeiras presidenciais e ambientes de comando que transmitem autoridade para fornecedores e parceiros. Porque grandes negócios começam antes da primeira palavra.
            </p>
            <p className="text-white/70 text-sm mb-8">
              Projeto 3D gratuito · Equipe própria de montagem · Suporte local em Caruaru e Agreste
            </p>
            <a
              href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Diretoria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-8 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide"
            >
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-bold">
              Por que líderes empresariais escolhem a <span className="text-[#E8610A]">Home Office Premium</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b) => (
              <div key={b.title} className="bg-white rounded-xl p-6 flex flex-col gap-4 border border-gray-200">
                <div className="w-12 h-12 flex items-center justify-center bg-[#E8610A]/10 rounded-lg">
                  <i className={`${b.icon} text-[#E8610A] text-2xl`}></i>
                </div>
                <h3 className="text-gray-900 font-bold text-base leading-snug">{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#fafafa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <h2 className="text-gray-900 text-2xl md:text-3xl font-normal leading-tight">
              <span className="text-[#E8610A] font-semibold">Ambientes de Comando</span> que Fecham Negócios
            </h2>
            <div className="w-16 h-0.5 bg-[#E8610A] mx-auto mt-4"></div>
          </div>
          <div className="flex flex-col gap-12">
            {blocks.map((block, idx) => (
              <div key={idx} className={`flex flex-col ${block.reverse ? 'lg:flex-row-reverse' : 'lg:flex-row'} items-center gap-8 bg-white rounded-xl p-8 border border-gray-200`}>
                <div className="w-full lg:w-1/2 h-64 md:h-72 rounded-lg overflow-hidden flex-shrink-0">
                  <img src={block.img} alt={block.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="w-full lg:w-1/2">
                  <h3 className="text-gray-900 text-xl font-bold mb-4 leading-snug">{block.title}</h3>
                  <div className="flex flex-col gap-3">
                    {block.text.map((p, i) => (
                      <p key={i} className="text-gray-600 text-sm leading-relaxed">{p}</p>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-10">
            <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Diretoria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#E8610A] hover:bg-[#C4500A] text-white font-bold px-10 py-4 rounded-md transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
              <i className="ri-whatsapp-line text-lg"></i>
              SOLICITAR ORÇAMENTO E PROJETO 3D GRATUITO
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#1a1a1a]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-[#E8610A] text-sm font-bold uppercase tracking-widest mb-4">Sem custo e sem compromisso</p>
          <h2 className="text-white text-3xl md:text-4xl font-bold leading-tight mb-4">
            Veja seu escritório de diretoria em 3D antes de investir.
          </h2>
          <p className="text-gray-400 text-base mb-10">
            Nossa equipe visita sua empresa, entende sua visão e entrega o projeto 3D completo em até 48h. Você aprova cada detalhe — da mesa de reunião à recepção — antes de fabricarmos.
          </p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Diretoria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ebe5d] text-white font-bold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm uppercase tracking-wide">
            <i className="ri-whatsapp-line text-lg"></i>
            QUERO MEU PROJETO 3D GRATUITO
          </a>
        </div>
      </section>

      <section className="py-16 bg-[#1a1a1a]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-gray-400 text-sm mb-2">Galeria</p>
            <h2 className="text-white text-2xl md:text-3xl font-bold">Ambientes <span className="text-[#E8610A]">Executivos Premium</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {galleryImages.map((img, idx) => (
              <div key={idx} className="w-full h-64 md:h-80 rounded-xl overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-gray-400 text-xs font-semibold uppercase tracking-widest mb-3">SUPORTE LOCAL · CARUARU E AGRESTE</p>
          <h2 className="text-gray-900 text-4xl md:text-5xl font-bold mb-4">Solicite seu orçamento</h2>
          <p className="text-gray-500 text-base mb-8">Projeto 3D gratuito incluso. Resposta em até 2 horas.</p>
          <a href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Diretoria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white font-semibold px-10 py-4 rounded-full transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm mb-10">
            <i className="ri-whatsapp-line text-lg"></i>
            Chamar no WhatsApp
          </a>
          <div className="mt-10 text-gray-500 text-sm">
            <p>Av. Agamenon Magalhães, 1519 · Maurício de Nassau, Caruaru - PE · CEP 55014-000</p>
          </div>
        </div>
      </section>

      <footer className="bg-white py-8 border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-4">
          <img src="https://files.greatpages.com.br/arquivos/paginas_editor/98015-32d41c6ba8b126a2913863e7b342fb04.png" alt="Home Office Premium" className="h-16 w-auto object-contain" />
          <div className="flex items-center gap-4">
            <a href="https://www.instagram.com/homeoffice.premium/" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-cc1918b73804ac63dbfe7624bf53ae73.png" alt="Instagram" className="w-8 h-8 object-contain" /></a>
            <a href="https://www.facebook.com/homeoffice.premium" target="_blank" rel="noopener noreferrer" className="cursor-pointer"><img src="https://files.greatpages.com.br/arquivos/paginas_editor/1-d39cee63d39805fee1ea3fe03b5a2e09.png" alt="Facebook" className="w-8 h-8 object-contain" /></a>
          </div>
        </div>
      </footer>

      <a ref={whatsappBtnRef} href="https://wa.me/5581982030285?text=Ol%C3%A1%2C%20vim%20da%20LP%20de%20Escrit%C3%B3rios%20de%20Diretoria%20e%20quero%20solicitar%20meu%20or%C3%A7amento%20e%20projeto%203D%20gratuito!" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-[9999] w-14 h-14 flex items-center justify-center bg-[#25D366] rounded-full cursor-pointer hover:scale-110 transition-transform duration-200" aria-label="WhatsApp" style={{ display: 'flex' }}>
        <i className="ri-whatsapp-line text-white text-2xl"></i>
      </a>
    </div>
  );
}
